<template>
  <div id="app-main-container">
    <div v-if="isPublicPage && isLightCom && !$route.meta?.hideBanner">
      <div
        ref="upperBanner"
        class="app-upper-banner"
      >
        <a
          href="#"
          class="banner-link"
          @click.prevent="openLightComLink"
        >
          <img
            src="@/assets/images/banner/login_uppper.jpg"
            alt="Banner Superior"
            class="banner-img"
          >
        </a>
      </div>
    </div>
    <main class="app-router-content">
      <router-view />
    </main>
    <div v-if="isLightCom">
      <div v-if="isPublicPage">
        <div
          ref="lowerBanner"
          class="app-lower-banner"
        >
          <a
            href="#"
            class="banner-link"
            @click.prevent="openLightComLink"
          >
            <img
              src="@/assets/images/banner/login_lower.jpg"
              alt="Banner Inferior Login"
              class="banner-img"
            >
          </a>
        </div>
      </div>
      <div v-else>
        <div
          ref="lowerBanner"
          class="app-lower-banner"
        >
          <a
            href="#"
            class="banner-link"
            @click.prevent="openLightComLink"
          >
            <img
              src="@/assets/images/banner/home_lower.jpg"
              alt="Banner Inferior Home"
              class="banner-img"
            >
          </a>
        </div>
      </div>
    </div>

    <!-- Global Loading Overlay -->
    <div
      v-if="showGlobalLoading"
      class="global-loading-overlay"
    >
      <div class="global-loading-spinner"></div>
      <p class="global-loading-text">Carregando...</p>
    </div>

    <!-- Global Error Modal -->
    <div
      v-if="showErrorModal"
      class="global-error-backdrop"
    >
      <div class="global-error-card">
        <div class="global-error-header">
          <h5 class="global-error-title">
            {{ getErrorTitle() }}
          </h5>
          <button
            class="global-error-x"
            @click="closeErrorModal"
          >
            &times;
          </button>
        </div>
        <div class="global-error-body">
          <p class="global-error-message">
            {{ getErrorMessage() }}
          </p>
          <p class="global-error-hint">
            {{ getErrorHint() }}
          </p>
        </div>
        <div class="global-error-footer">
          <button
            v-if="globalErrorData.retry"
            class="global-error-btn global-error-btn-retry"
            @click="retryAction"
          >
            &#x21bb; {{ $t('error_modal.try_again') || 'Tentar novamente' }}
          </button>
          <button
            class="global-error-btn global-error-btn-close"
            @click="closeErrorModal"
          >
            {{ $t('error_modal.close') || 'Fechar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { captureException } from '@/errors'
  import fingerPrint from '@/services/fingerPrint'
  import globalUI from '@/services/globalUI'
  import { sessionRecorder, UserEvent, SESSION_EVENTS } from '@/services/sessionRecorder'
  import { sendDeviceInfo } from '@/views/home/service/sendDeviceInfo'

  const REDIRECT_LINK = import.meta.env.VITE_REDIRECT_LINK

  export default {
    name: 'App',
    data() {
      return {
        language: 'pt-BR',
        external: false,
        token: null,
        terminalToken: null,
        pointName: null,
        pathname: null,
        search: null,
        bannerResizeObserver: null,
        upperBannerDimensions: '0 x 0 px | n/a',
        lowerBannerDimensions: '0 x 0 px | n/a',
        redirectLink: REDIRECT_LINK,
        pointIdTokenSelection: sessionStorage.getItem('point_id_token_selection') || null,
        showGlobalLoading: false,
        showErrorModal: false,
        globalErrorData: {}
      }
    },
    computed: {
      isPublicPage() {
        return !!this.$route?.meta?.public
      },
      isLightCom() {
        const val = this.pointIdTokenSelection
        if (!val) return false

        const envPoints = import.meta.env.VITE_LIGHTCOM_POINTS
        const lightComPoints = envPoints.split(',').map(p => p.trim())

        return lightComPoints.some(point => val.includes(point))
      }
    },
    watch: {
      isLightCom() {
        this.$nextTick(() => {
          this.setupBannerObserver()
          this.updateBannerHeights()
        })
      },
      isPublicPage() {
        this.$nextTick(() => {
          this.setupBannerObserver()
          this.updateBannerHeights()
        })
      }
    },
    async created() {
      // Registrar subscription globalUI ANTES de qualquer child mount
      // Isso é crítico: created() dispara parent-first, mounted() child-first.
      this._unsubLoading = globalUI.onLoading(show => {
        this.showGlobalLoading = show
      })
      this._unsubError = globalUI.onError(data => {
        if (data) {
          this.globalErrorData = data
          this.showErrorModal = true
        } else {
          this.showErrorModal = false
          this.globalErrorData = {}
        }
      })

      const originalSetItem = sessionStorage.setItem
      sessionStorage.setItem = (key, value) => {
        originalSetItem.call(sessionStorage, key, value)
        if (key === 'point_id_token_selection') {
          this.pointIdTokenSelection = value ? String(value) : null
        }
      }

      const originalRemoveItem = sessionStorage.removeItem
      sessionStorage.removeItem = key => {
        originalRemoveItem.call(sessionStorage, key)
        if (key === 'point_id_token_selection') {
          this.pointIdTokenSelection = null
        }
      }

      localStorage.removeItem('show_payment_info')
      try {
        let fingerPrintUUID = sessionStorage.getItem('fingerPrintUUID')
        if (!fingerPrintUUID) {
          fingerPrintUUID = await fingerPrint()
          localStorage.setItem('fingerPrintUUID', fingerPrintUUID)
        }
        if (sessionStorage.getItem('hasDevice') !== 'hasDevice') {
          sendDeviceInfo(fingerPrintUUID)
          sessionStorage.setItem('hasDevice', 'hasDevice')
        }
      } catch (error) {
        captureException('Send device info error', error)
      }
    },
    mounted() {
      this.setupBannerObserver()
      this.updateBannerHeights()
      window.addEventListener('resize', this.updateBannerHeights)
    },
    beforeUnmount() {
      if (this.bannerResizeObserver) {
        this.bannerResizeObserver.disconnect()
        this.bannerResizeObserver = null
      }

      window.removeEventListener('resize', this.updateBannerHeights)
      this.resetBannerHeights()

      // Cancelar inscrições dos eventos globais
      if (this._unsubLoading) {
        this._unsubLoading()
      }
      if (this._unsubError) {
        this._unsubError()
      }
    },
    methods: {
      getErrorTitle() {
        const data = this.globalErrorData
        if (!data || !data.type) return this.$t('error_modal.generic_error_title') || 'Erro'
        const map = {
          network: 'error_modal.no_internet_title',
          timeout: 'error_modal.request_timeout_title',
          server: 'error_modal.server_unreachable_title',
          generic: 'error_modal.generic_error_title'
        }
        return this.$t(map[data.type] || 'error_modal.generic_error_title')
      },
      getErrorHint() {
        const data = this.globalErrorData
        if (!data || !data.type) return this.$t('error_modal.generic_hint') || ''
        const map = {
          network: 'error_modal.no_internet_hint',
          timeout: 'error_modal.request_timeout_hint',
          server: 'error_modal.server_unreachable_hint',
          generic: 'error_modal.generic_hint'
        }
        return this.$t(map[data.type] || 'error_modal.generic_hint')
      },
      getErrorMessage() {
        if (this.globalErrorData && this.globalErrorData.message) return this.globalErrorData.message
        return this.$t('error_modal.generic_error_message') || 'Ocorreu um erro inesperado.'
      },
      closeErrorModal() {
        this.showErrorModal = false
        this.globalErrorData = {}
        globalUI.hideError()
      },
      retryAction() {
        const fn = this.globalErrorData && this.globalErrorData.retry
        this.closeErrorModal()
        if (typeof fn === 'function') fn()
      },
      openLightComLink() {
        const url = import.meta.env.VITE_LIGHTCOM_URL || 'https://www.lightcom.com.br/'
        sessionRecorder.addUserEvent(new UserEvent(SESSION_EVENTS.LIGHTCOM_LINK_OPENED, null, { url }))
        window.open(url, '_blank', 'noopener,noreferrer')
      },
      setupBannerObserver() {
        if (this.bannerResizeObserver) {
          this.bannerResizeObserver.disconnect()
        }

        const topBanner = this.$refs.upperBanner
        const bottomBanner = this.$refs.lowerBanner

        if (!topBanner && !bottomBanner) {
          return
        }

        if (typeof ResizeObserver === 'undefined') {
          return
        }

        this.bannerResizeObserver = new ResizeObserver(() => {
          this.updateBannerHeights()
        })

        if (topBanner) {
          this.bannerResizeObserver.observe(topBanner)
        }

        if (bottomBanner) {
          this.bannerResizeObserver.observe(bottomBanner)
        }
      },
      updateBannerHeights() {
        const topRect = this.$refs.upperBanner?.getBoundingClientRect()
        const bottomRect = this.$refs.lowerBanner?.getBoundingClientRect()
        const topHeight = topRect?.height ?? 0
        const bottomHeight = bottomRect?.height ?? 0

        const topWidth = topRect?.width ?? 0
        const bottomWidth = bottomRect?.width ?? 0
        this.upperBannerDimensions = this.formatBannerDimensions(topWidth, topHeight)
        this.lowerBannerDimensions = this.formatBannerDimensions(bottomWidth, bottomHeight)

        document.documentElement.style.setProperty('--app-banner-top-height', `${Math.round(topHeight)}px`)
        document.documentElement.style.setProperty('--app-banner-bottom-height', `${Math.round(bottomHeight)}px`)
      },
      formatBannerDimensions(width, height) {
        const roundedWidth = Math.round(width)
        const roundedHeight = Math.round(height)

        if (!roundedWidth || !roundedHeight) {
          return `${roundedWidth} x ${roundedHeight} px | n/a`
        }

        const ratio = roundedWidth / roundedHeight
        const ratioStr = Number(ratio.toFixed(2)).toString()

        return `Dimensões do Banner: ${roundedWidth} x ${roundedHeight} px | ${ratioStr}`
      },
      resetBannerHeights() {
        document.documentElement.style.setProperty('--app-banner-top-height', '0px')
        document.documentElement.style.setProperty('--app-banner-bottom-height', '0px')
      }
    }
  }
</script>

<style lang="scss">
  html,
  body {
    margin: 0 !important;
  }

  #app-main-container {
    max-width: 740px;
    margin: 0 auto;
    min-height: 100vh;
    min-height: 100dvh;
    background-color: white;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    overflow-x: hidden;
    position: relative;
  }

  .app-upper-banner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-height: 184px;
    margin: 0 auto;
    overflow: hidden;
    z-index: 100;
    background: #777777;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .app-lower-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-height: 184px;
    margin: 0 auto;
    overflow: hidden;
    z-index: 100;
    background: #777777;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  .banner-link {
    width: 100%;
    height: 100%;
    display: block;
  }

  .banner-dimensions {
    font-size: 12px;
    font-weight: 600;
  }

  .banner-img {
    width: 100%;
    height: 100%;
    max-height: 184px;
    object-fit: contain;
    display: block;
  }

  /* ═══════════════════════════════════════════
   Global Loading Overlay
   ═══════════════════════════════════════════ */
  .global-loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    height: 100dvh;
    background: rgba(255, 255, 255, 0.92);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }

  .global-loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e0e0e0;
    border-top: 4px solid #f58a48;
    border-radius: 50%;
    animation: global-spin 0.8s linear infinite;
  }

  @keyframes global-spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .global-loading-text {
    margin-top: 16px;
    color: #424242;
    font-size: 16px;
    font-weight: 500;
  }

  /* ═══════════════════════════════════════════
   Global Error Modal (custom)
   ═══════════════════════════════════════════ */
  .global-error-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    height: 100dvh;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 24px;
  }

  .global-error-card {
    background: #fff;
    border-radius: 12px;
    max-width: 360px;
    width: 100%;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    position: relative;
  }

  .global-error-header {
    padding: 20px 20px 0;
    text-align: center;
    position: relative;
  }

  .global-error-x {
    position: absolute;
    top: 8px;
    right: 12px;
    background: none;
    border: none;
    font-size: 24px;
    color: #999;
    cursor: pointer;
    padding: 4px 8px;
    line-height: 1;
  }

  .global-error-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #333;
  }

  .global-error-body {
    padding: 20px 24px 16px;
    text-align: center;
  }

  .global-error-message {
    font-size: 15px;
    color: #555;
    line-height: 1.5;
    margin: 0;
  }

  .global-error-hint {
    font-size: 12px;
    color: #aaa;
    margin: 12px 0 0;
  }

  .global-error-footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 20px 20px;
  }

  .global-error-btn {
    display: block;
    width: 100%;
    padding: 12px 20px;
    border: none;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
  }

  .global-error-btn-retry {
    background: #f58a48;
    color: #fff;
  }

  .global-error-btn-close {
    background: #e9ecef;
    color: #333;
  }
</style>
